import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useInstallPrompt } from '../hooks/useInstallPrompt.js';
import { useState } from 'react';
import SEO from '../components/SEO.js';
import { useAuthStore } from '../stores/auth-store.js';
import BottomNav from '../components/BottomNav.js';
import { usePageTracker } from '../hooks/usePageTracker.js';

function MobileInstallButton() {
  const { canInstall, isIOS, isMobile, promptInstall, hasNativePrompt } = useInstallPrompt();
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  if (!canInstall || !isMobile) return null;

  if (showIOSGuide) {
    return (
      <div className="mt-3 rounded-xl px-5 py-4 text-left" style={{ background: 'rgba(255, 240, 235, 0.06)', border: '1px solid rgba(255, 107, 53, 0.2)' }}>
        <SEO path="/" />
        <p className="text-[#f5f0eb] text-sm font-semibold mb-2">Add to Home Screen</p>
        <ol className="text-[#c5bfb9] text-xs space-y-1.5">
          <li>1. Tap the <strong className="text-white">Share</strong> button at the bottom of Safari</li>
          <li>2. Scroll down, tap <strong className="text-white">"Add to Home Screen"</strong></li>
          <li>3. Tap <strong className="text-white">"Add"</strong> — open anytime from your home screen</li>
        </ol>
        <button onClick={() => setShowIOSGuide(false)} className="text-[#8a8480] text-xs mt-2">Got it</button>
      </div>
    );
  }

  return (
    <button
      onClick={() => hasNativePrompt ? promptInstall() : isIOS ? setShowIOSGuide(true) : null}
      className="mt-3 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm text-[#f5f0eb] transition-all hover:-translate-y-0.5"
      style={{ background: 'rgba(255, 240, 235, 0.06)', border: '1px solid rgba(255, 240, 235, 0.15)' }}
    >
      <svg className="w-4 h-4 text-[#ff6b35]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
      </svg>
      Install App
    </button>
  );
}

export default function LandingPage() {
  usePageTracker('/');

  const { token } = useAuthStore();
  const isLoggedIn = !!token;

  return (
    <div className="min-h-screen text-[#f5f0eb] overflow-hidden" style={{ background: '#0c0a09' }}>

      {/* ═══ ANIMATED GLOW ORBS ═══ */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute w-[700px] h-[700px] -top-[180px] -right-[50px] rounded-full opacity-50 animate-[float_8s_ease-in-out_infinite]" style={{
          background: 'radial-gradient(circle, #ff6b35, transparent 70%)', filter: 'blur(100px)'
        }} />
        <div className="absolute w-[600px] h-[600px] -bottom-[100px] -left-[50px] rounded-full opacity-35 animate-[float_8s_ease-in-out_infinite_-4s]" style={{
          background: 'radial-gradient(circle, #4c6ef5, transparent 70%)', filter: 'blur(100px)'
        }} />
        {/* Subtle center glow for depth */}
        <div className="absolute w-[400px] h-[400px] top-[40%] left-[50%] -translate-x-1/2 rounded-full opacity-15" style={{
          background: 'radial-gradient(circle, #ff6b35, transparent 70%)', filter: 'blur(120px)'
        }} />
      </div>

      {/* ═══ NOISE TEXTURE ═══ */}
      <div className="fixed inset-0 pointer-events-none z-[1] opacity-[0.035]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat', backgroundSize: '256px 256px'
      }} />

      {/* ═══ NAV ═══ */}
      <nav className="relative z-10 max-w-7xl mx-auto w-full px-6 sm:px-8 pt-5 pb-2 flex items-center justify-between">
        <span className="text-[#ff6b35] font-bold text-lg tracking-tight">AutoCarPost</span>
        <Link to="/login" className="text-[#9a9590] text-sm hover:text-[#f5f0eb] transition-colors">Sign in</Link>
      </nav>

      {/* ═══ HERO — headline left, card right ═══ */}
      <section className="relative z-10 max-w-6xl mx-auto w-full px-6 sm:px-8 pt-4 pb-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">

          {/* Left — headline + subtext + CTA */}
          <div className="text-center lg:text-left">
            <h1 className="mb-5" style={{ letterSpacing: '-0.03em', lineHeight: '0.92' }}>
              <span className="block font-black" style={{ fontSize: 'clamp(3.5rem, 9vw, 7rem)' }}>Scan.</span>
              <span className="block font-black" style={{ fontSize: 'clamp(3.5rem, 9vw, 7rem)' }}>Snap.</span>
              <span className="block font-black" style={{
                fontSize: 'clamp(3.5rem, 9vw, 7rem)',
                background: 'linear-gradient(135deg, #ff6b35 0%, #ff3d57 60%, #ffaa6b 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'
              }}>Sell.</span>
            </h1>

            <p className="text-[#d5d0cb] text-lg sm:text-xl mb-2">
              Hidden automation for <span className="text-[#f5f0eb] font-semibold">instant car listings</span>
            </p>
            <p className="text-[#a5a09b] text-sm leading-relaxed mb-7 max-w-md mx-auto lg:mx-0">
              Scan your VIN, snap photos, and AI lists your car on every marketplace in seconds. No paperwork. No research. Just sell.
            </p>

            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 mb-3">
              <Link to="/go" className="cta-glow px-8 py-3.5 rounded-xl font-bold text-base text-white transition-all hover:-translate-y-0.5 hover:scale-[1.02]" style={{
                background: 'linear-gradient(135deg, #ff6b35 0%, #ff3d57 100%)',
                boxShadow: '0 0 40px rgba(255, 107, 53, 0.35), 0 4px 20px rgba(0, 0, 0, 0.4)'
              }}>Get Started Free</Link>
              <Link to="/browse" className="px-6 py-3.5 rounded-xl font-semibold text-sm text-[#c5bfb9] transition-all hover:-translate-y-0.5 hover:text-white hover:border-[#ff6b3540]" style={{
                background: 'rgba(255, 240, 235, 0.04)', border: '1px solid rgba(255, 240, 235, 0.12)',
              }}>Browse Cars</Link>
            </div>
            <p className="text-[#8a8480] text-xs">Free Forever · No Dealer Fees · No Catch</p>
            <MobileInstallButton />
          </div>

          {/* Right column — Car card */}
          <div className="relative pt-4" style={{ perspective: '1200px' }}>
            {/* Floating notification — top right */}
            <div className="hidden lg:flex absolute -top-2 right-0 z-20 items-center gap-2 px-3 py-2 rounded-xl animate-[toast-float_4s_ease-in-out_infinite]" style={{
              background: 'rgba(20, 16, 14, 0.9)', border: '1px solid rgba(52, 211, 153, 0.25)',
              backdropFilter: 'blur(12px)', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)'
            }}>
              <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </div>
              <div>
                <p className="text-[#f5f0eb] text-xs font-semibold">2019 BMW 3 Series</p>
                <p className="text-emerald-400 text-[10px]">Sold in 8 hours</p>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 mt-8" style={{
              background: 'rgba(20, 16, 14, 0.95)',
              border: '1px solid rgba(255, 107, 53, 0.18)',
              boxShadow: '0 0 80px rgba(255, 107, 53, 0.12), 0 0 40px rgba(255, 107, 53, 0.06), 0 20px 60px rgba(0, 0, 0, 0.7)',
              transform: 'rotateY(-3deg) rotateX(1deg)'
            }}>
              <div className="relative h-48 sm:h-56 overflow-hidden">
                <img src="/car-hero.jpg" alt="2021 Tesla Model 3" className="w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(20, 16, 14, 1) 0%, rgba(20, 16, 14, 0.2) 40%, transparent 70%)' }} />
                <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-lg" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }}>
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-emerald-400 text-xs font-semibold uppercase tracking-wider">Live</span>
                </div>
                <div className="absolute top-3 right-3 text-[#b5b0ab] text-xs font-mono px-2 py-1 rounded-lg" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }}>47s ago</div>
              </div>

              <div className="px-5 pb-5 -mt-4 relative z-10">
                <div className="flex items-baseline justify-between gap-3 mb-1.5">
                  <h3 className="text-[#f5f0eb] font-bold text-xl">2021 Tesla Model 3</h3>
                  <span className="text-[#ff6b35] font-bold text-xl whitespace-nowrap">$38,500</span>
                </div>
                <p className="text-[#c5bfb9] text-sm mb-1.5">Long Range · 25,000 mi · Clean Title</p>
                <p className="text-[#9a9590] text-xs mb-3">AI-generated: "Excellent condition, fully loaded with autopilot..."</p>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[#9a9590] text-xs">Posted to</span>
                  {[
                    { name: 'Marketplace', color: '#3b82f6' },
                    { name: 'Craigslist', color: '#f97316' },
                    { name: 'AutoTrader', color: '#ef4444' }
                  ].map(p => (
                    <span key={p.name} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-semibold" style={{
                      background: `${p.color}18`, color: p.color, border: `1px solid ${p.color}30`
                    }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.color }} />
                      {p.name}
                    </span>
                  ))}
                </div>
              </div>

            </div>

            {/* QR badge — hanging off bottom-right corner of card */}
            <div className="hidden lg:block absolute -bottom-14 -right-14 z-30">
              <div className="flex flex-col items-center gap-1.5 rounded-2xl p-3.5" style={{
                background: 'rgba(12, 10, 9, 0.92)',
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(255, 107, 53, 0.25)',
                boxShadow: '0 0 50px rgba(255, 107, 53, 0.12), 0 12px 40px rgba(0, 0, 0, 0.5)'
              }}>
                <div className="bg-white rounded-lg p-2.5">
                  <QRCodeSVG value="https://autocarpost.com/go" size={90} level="M" />
                </div>
                <p className="text-[#c5bfb9] text-[10px] font-semibold tracking-wide uppercase">Scan to Start</p>
              </div>
            </div>

            {/* Floating notification — bottom left */}
            <div className="hidden lg:flex absolute -bottom-6 left-4 z-20 items-center gap-2 px-3 py-2 rounded-xl animate-[toast-float_4s_ease-in-out_infinite_-2s]" style={{
              background: 'rgba(20, 16, 14, 0.9)', border: '1px solid rgba(76, 110, 245, 0.25)',
              backdropFilter: 'blur(12px)', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)'
            }}>
              <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <svg className="w-3.5 h-3.5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
              </div>
              <div>
                <p className="text-[#f5f0eb] text-xs font-semibold">2022 Honda Civic</p>
                <p className="text-blue-400 text-[10px]">Just listed — $24,900</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS — full-width 3-col strip ═══ */}
      <section className="relative z-10 max-w-6xl mx-auto w-full px-6 sm:px-8 pb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { num: '01', title: 'Scan', desc: 'Point your phone at the VIN barcode. We decode year, make, model, trim instantly.', color: '#ff6b35' },
            { num: '02', title: 'Snap', desc: 'Take a few photos. AI writes a professional listing with market-competitive pricing.', color: '#4c6ef5' },
            { num: '03', title: 'Sell', desc: 'Your listing goes live on Facebook Marketplace, Craigslist, AutoTrader — automatically.', color: '#34d399' },
          ].map((step) => (
            <div key={step.title} className="rounded-xl px-5 py-4 flex gap-4 items-start" style={{
              background: 'rgba(255, 240, 235, 0.03)', border: '1px solid rgba(255, 240, 235, 0.06)'
            }}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                background: `${step.color}12`, border: `1px solid ${step.color}25`,
              }}>
                <span className="font-mono font-bold text-sm" style={{ color: step.color }}>{step.num}</span>
              </div>
              <div>
                <h3 className="text-[#f5f0eb] font-bold text-sm mb-1">{step.title}</h3>
                <p className="text-[#9a9590] text-xs leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ CTA BANNER — full width ═══ */}
      <section className="relative z-10 max-w-6xl mx-auto w-full px-6 sm:px-8 pt-2 pb-10">
        <div className="rounded-2xl px-8 py-10 sm:px-12 flex flex-col sm:flex-row items-center gap-8 relative overflow-hidden" style={{
          background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.08) 0%, rgba(20, 16, 14, 0.98) 50%, rgba(76, 110, 245, 0.06) 100%)',
          border: '1px solid rgba(255, 107, 53, 0.12)',
          boxShadow: '0 0 60px rgba(255, 107, 53, 0.06), 0 8px 32px rgba(0, 0, 0, 0.4)'
        }}>
          <div className="absolute -top-20 -left-20 w-60 h-60 rounded-full opacity-20 pointer-events-none" style={{
            background: 'radial-gradient(circle, #ff6b35, transparent 70%)', filter: 'blur(60px)'
          }} />
          <div className="flex-1 text-center sm:text-left relative z-10">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2">Ready to sell your car?</h2>
            <p className="text-[#b5b0ab] text-sm mb-5">List your car in seconds, not hours. Free forever.</p>
            <Link to="/go" className="inline-block px-8 py-3.5 rounded-xl font-bold text-base text-white transition-all hover:-translate-y-0.5 hover:scale-[1.02]" style={{
              background: 'linear-gradient(135deg, #ff6b35 0%, #ff3d57 100%)',
              boxShadow: '0 0 30px rgba(255, 107, 53, 0.3), 0 4px 16px rgba(0, 0, 0, 0.3)'
            }}>Get Started Free</Link>
          </div>
          <div className="hidden sm:flex flex-col items-center gap-1 flex-shrink-0 relative z-10 text-center">
            <svg className="w-16 h-16 text-[#ff6b35] opacity-60 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
            </svg>
            <p className="text-[#c5bfb9] text-sm font-medium">Works on any phone</p>
            <p className="text-[#8a8480] text-xs">No app download needed</p>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="relative z-10 py-4" style={{ borderTop: '1px solid rgba(255, 240, 235, 0.06)' }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-4 text-[#8a8480] text-xs">
            <Link to="/terms" className="hover:text-[#c5bfb9] transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-[#c5bfb9] transition-colors">Privacy</Link>
            <Link to="/acceptable-use" className="hover:text-[#c5bfb9] transition-colors">Acceptable Use</Link>
          </div>
          <p className="text-[#8a8480] text-xs opacity-60">&copy; {new Date().getFullYear()} AutoCarPost</p>
        </div>
      </footer>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -20px) scale(1.05); }
          66% { transform: translate(-20px, 15px) scale(0.95); }
        }
        @keyframes cta-pulse {
          0%, 100% { box-shadow: 0 0 40px rgba(255, 107, 53, 0.35), 0 4px 20px rgba(0, 0, 0, 0.4); }
          50% { box-shadow: 0 0 60px rgba(255, 107, 53, 0.5), 0 4px 20px rgba(0, 0, 0, 0.4); }
        }
        .cta-glow { animation: cta-pulse 3s ease-in-out infinite; }
        @keyframes toast-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
      `}</style>
          {isLoggedIn && <BottomNav />}
    </div>
  );
}
